import Style from './data-table.tailwind.js';

const DEFAULT_PAGE_SIZES = [25, 50, 100];
const INTERACTIVE_SELECTOR = 'a,button,input,select,textarea,[role="button"]';

function SortIndicator({ active, dir }) {
  return (
    <span className={`${Style.sortBase} ${active ? Style.sortActive : Style.sortInactive}`}>
      {active ? (dir === 'asc' ? '↑' : '↓') : '↕'}
    </span>
  );
}

function PageButton({ onClick, disabled, ariaLabel, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      className={`${Style.pageBtnBase} ${disabled ? Style.pageBtnOff : Style.pageBtnOn}`}
    >
      {children}
    </button>
  );
}

export function DataTablePagination({
  page,
  pageSize,
  totalPages,
  onPageChange,
  onPageSizeChange,
  pageSizes = DEFAULT_PAGE_SIZES,
}) {
  return (
    <div className={Style.pagination}>
      <div className={Style.pageSizeGroup}>
        <span>Rows per page</span>
        <select
          value={pageSize}
          onChange={e => onPageSizeChange(Number(e.target.value))}
          className={Style.pageSizeSelect}
        >
          {pageSizes.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      <div className={Style.pageCtrlGroup}>
        <span>Page</span>
        <input
          type="number"
          min={1}
          max={totalPages}
          value={page}
          onChange={e => {
            const v = Number(e.target.value);
            if (v >= 1 && v <= totalPages) onPageChange(v);
          }}
          className={Style.pageInput}
        />
        <span>of {totalPages}</span>
        <PageButton onClick={() => onPageChange(1)}          disabled={page <= 1}            ariaLabel="First page">«</PageButton>
        <PageButton onClick={() => onPageChange(page - 1)}   disabled={page <= 1}            ariaLabel="Previous page">‹</PageButton>
        <PageButton onClick={() => onPageChange(page + 1)}   disabled={page >= totalPages}   ariaLabel="Next page">›</PageButton>
        <PageButton onClick={() => onPageChange(totalPages)} disabled={page >= totalPages}   ariaLabel="Last page">»</PageButton>
      </div>
    </div>
  );
}

export function DataTable({
  columns,
  rows,
  loading,
  emptyMessage = 'No results found.',
  renderCell,
  onRowClick,
  onRowAuxClick,
  sortKey,
  sortDir,
  onSort,
  page,
  pageSize,
  totalPages,
  onPageChange,
  onPageSizeChange,
  pageSizes,
}) {
  return (
    <div className={Style.container}>
      <div className={Style.scroll}>
        <table className={Style.table}>

          <thead>
            <tr className={Style.thead}>
              {columns.map(col => (
                <th
                  key={col.key}
                  aria-sort={col.sortable
                    ? (sortKey === col.key ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none')
                    : undefined}
                  className={Style.th}
                >
                  {col.sortable ? (
                    <button onClick={() => onSort(col.key)} className={Style.thSortBtn}>
                      {col.label}
                      <SortIndicator active={sortKey === col.key} dir={sortDir} />
                    </button>
                  ) : (
                    <span className={Style.thLabel}>{col.label}</span>
                  )}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan={columns.length} className={Style.empty}>Loading…</td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className={Style.empty}>{emptyMessage}</td>
              </tr>
            ) : rows.map((row, rowIndex) => (
              <tr
                key={row.id ?? rowIndex}
                className={`${Style.row}${onRowClick ? ' cursor-pointer' : ''}`}
                role={onRowClick ? 'button' : undefined}
                tabIndex={onRowClick ? 0 : undefined}
                onKeyDown={onRowClick ? e => {
                  const interactive = e.target.closest(INTERACTIVE_SELECTOR);
                  if (interactive && interactive !== e.currentTarget) return;
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onRowClick(row, e);
                  }
                } : undefined}
                onClick={onRowClick ? e => {
                  const interactive = e.target.closest(INTERACTIVE_SELECTOR);
                  if (interactive && interactive !== e.currentTarget) return;
                  onRowClick(row, e);
                } : undefined}
                onAuxClick={onRowAuxClick ? e => {
                  if (e.button !== 1) return;
                  const interactive = e.target.closest(INTERACTIVE_SELECTOR);
                  if (interactive && interactive !== e.currentTarget) return;
                  onRowAuxClick(row, e);
                } : undefined}
              >
                {columns.map(col => {
                  if (renderCell) {
                    const cell = renderCell(col.key, row[col.key], row);
                    if (cell != null) return cell;
                  }
                  return (
                    <td key={col.key} className={Style.cell}>
                      {row[col.key] ?? '—'}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>

        </table>
      </div>

      {totalPages != null && (
        <DataTablePagination
          page={page}
          pageSize={pageSize}
          totalPages={totalPages}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          pageSizes={pageSizes}
        />
      )}
    </div>
  );
}
